<script setup>
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableRowRounded from "@/components/global-themed/table/TableRowRounded.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import ApprovedByPill from "@/components/kit-activity-log/partials/ApprovedByPill.vue";
import ActivityTarget from "@/components/kit-activity-log/partials/ActivityTarget.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";

import useDates from "@/composables/useDates";
import {changeOrderBy, useKitActivityLogs} from "@/composables/kit-activity-logs/useKitActivityLogs";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import ActivityLogButton from "@/components/kit-activity-log/partials/ActivityLogButton.vue";
const {formattedDate} = useDates()
const {orderBy, orderDirection, kitActivityLogs, isPending} = useKitActivityLogs();

const isAutomatedActionNotImplemented = (activityLog) => {
  return activityLog.activity.type === 'automatedactionfix'
    && !activityLog.activity.manual_implementation
    && !activityLog.activity.implemented_at;
};

const isManualImplementationRequired = (activityLog) => {
  return activityLog.activity.manual_implementation;
}

const shouldBeGreydOut = (activityLog) => {
  if (activityLog.undone_at || activityLog?.location?.deleted_at) {
    return true;
  }

  return isAutomatedActionNotImplemented(activityLog);
}

const getSubtext = (activityLog) => {
  if (activityLog.undone_at) {
    return `This change was reverted on ${formattedDate(activityLog.undone_at)} by ${activityLog.undone_by?.name || 'Unknown'}`;
  }

  if (activityLog?.location?.deleted_at) {
    return `This location has been deleted on ${formattedDate(activityLog.location.deleted_at)}`;
  }

  if (isAutomatedActionNotImplemented(activityLog)) {
    return 'Not implemented as the update was no longer required';
  }

  if (isManualImplementationRequired(activityLog)) {
    return 'Not implemented as this update requires manual action';
  }

  return null;
}
</script>

<template>
  <DataTable class="tw-w-full">
    <TableHeaderRow class="tw-grid-cols-16">
      <TableHeaderCell
        col-span="4"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="category"
        @order-changed="changeOrderBy"
      >
        <span>Target</span>
      </TableHeaderCell>

      <TableHeaderCell
        col-span="6"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="subcategory"
        @order-changed="changeOrderBy"
      >
        <span>Change</span>
      </TableHeaderCell>

      <TableHeaderCell
        col-span="3"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="created_by_id"
        @order-changed="changeOrderBy"
      >
        <span>Approved By</span>
      </TableHeaderCell>

      <TableHeaderCell
        col-span="3"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="created_at"
        @order-changed="changeOrderBy"
      >
        <span>Date</span>
      </TableHeaderCell>
    </TableHeaderRow>

    <div
      v-if="isPending"
      class="tw-bg-white tw-p-8 tw-rounded-lg"
    >
      <LoadingBlock />
    </div>

    <div
      v-else
      class="tw-flex tw-flex-col tw-gap-4 tw-text-neutral-900 tw-text-base tw-mt-8"
    >
      <ZeroContent v-if="kitActivityLogs?.length < 1">
        Looks like there are no changes to display.
      </ZeroContent>

      <TableRowRounded
        v-for="activityLog in kitActivityLogs"
        :key="activityLog.id"
        v-tooltip="{
          content: `This update couldn’t be applied automatically as it may affect your website’s design. <br/> Some elements, such as headings or layout-sensitive content, need to be changed manually to avoid unintended styling issues.`,
          placement: 'bottom',
          theme: 'dark-blue-tooltip',
          html: true,
          disabled: !isManualImplementationRequired(activityLog)
        }"
        :class="[
          'tw-grid-cols-16',
          {
            'tw-bg-neutral-100/50': shouldBeGreydOut(activityLog)
          }
        ]"
      >
        <TableCell
          col-span="4"
          :class="[
            'tw-flex tw-items-center tw-gap-4',
            {
              'tw-opacity-50': shouldBeGreydOut(activityLog)
            }
          ]"
        >
          <ActivityTarget
            :category="activityLog.category"
            :page="activityLog.page"
            :location="activityLog.location"
          />
        </TableCell>

        <TableCell
          col-span="6"
        >
          <div class="tw-flex tw-flex-col tw-gap-1">
            <span
              :class="{
                'tw-opacity-50': shouldBeGreydOut(activityLog)
              }"
            >
              {{ activityLog.subcategory }}
            </span>
            <span
              v-if="getSubtext(activityLog)"
              class="tw-text-sm tw-font-bold tw-text-neutral-500 tw-italic"
            >
              {{ getSubtext(activityLog) }}
            </span>
          </div>
        </TableCell>

        <TableCell
          col-span="3"
          :class="{
            'tw-opacity-50': shouldBeGreydOut(activityLog)
          }"
        >
          <ApprovedByPill
            :user="{name: activityLog.created_by}"
            :date="activityLog.created_at"
          />
        </TableCell>

        <TableCell col-span="3">
          <div class="tw-flex tw-items-center tw-gap-4">
            <span
              :class="{
                'tw-opacity-50': shouldBeGreydOut(activityLog)
              }"
            >
              {{ formattedDate(activityLog.created_at) }}
            </span>
            <ActivityLogButton
              v-if="!shouldBeGreydOut(activityLog)"
              :activity-log="activityLog"
            />
          </div>
        </TableCell>
      </TableRowRounded>
    </div>
  </DataTable>
</template>
